import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SubscriptionService, SubscriptionStatus } from '../../services/subscription.service';

@Component({
  selector: 'app-expiring-subscription-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (showBanner()) {
      <div class="banner-container">
        <div class="banner-content">
          <div class="banner-message">
            <span class="warning-icon">⏰</span>
            <div class="message-text">
              <strong>Dein Abonnement läuft bald ab!</strong>
              <p>Verbleibend: {{ daysRemaining() }} Tage</p>
            </div>
          </div>
          <div class="banner-actions">
            <button class="renew-button" (click)="onRenew()">
              Jetzt verlängern
            </button>
            <button class="close-button" (click)="closeBanner()" aria-label="Banner schließen">
              ✕
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .banner-container {
      width: 100%;
      background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
      border-left: 4px solid #ff9800;
      padding: 16px 20px;
      margin-bottom: 20px;
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(255, 152, 0, 0.15);
      animation: slideDown 0.3s ease-out;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .banner-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      max-width: 1200px;
      margin: 0 auto;
    }

    .banner-message {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
    }

    .warning-icon {
      font-size: 24px;
      flex-shrink: 0;
    }

    .message-text {
      margin: 0;
    }

    .message-text strong {
      display: block;
      color: #333;
      font-size: 15px;
      margin-bottom: 4px;
    }

    .message-text p {
      margin: 0;
      color: #666;
      font-size: 13px;
    }

    .banner-actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .renew-button {
      background-color: #4CAF50;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;
    }

    .renew-button:hover {
      background-color: #45a049;
      box-shadow: 0 2px 6px rgba(76, 175, 80, 0.3);
    }

    .renew-button:active {
      transform: scale(0.98);
    }

    .close-button {
      background: none;
      border: none;
      color: #999;
      font-size: 20px;
      cursor: pointer;
      padding: 4px 8px;
      transition: color 0.2s;
      flex-shrink: 0;
    }

    .close-button:hover {
      color: #333;
    }

    @media (max-width: 768px) {
      .banner-container {
        margin-bottom: 15px;
      }

      .banner-content {
        flex-direction: column;
        gap: 12px;
      }

      .banner-message {
        width: 100%;
      }

      .banner-actions {
        width: 100%;
        justify-content: space-between;
      }

      .renew-button {
        flex: 1;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpiringSubscriptionBannerComponent {
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);

  showBanner = signal(false);
  daysRemaining = signal(0);

  constructor() {
    this.loadSubscriptionStatus();
  }

  private loadSubscriptionStatus() {
    this.subscriptionService.getSubscriptionStatus().subscribe({
      next: (status: SubscriptionStatus) => {
        this.daysRemaining.set(status.daysRemaining);

        // Banner nur anzeigen wenn Abo aktiv und expiring soon (aus DB Config)
        if (status.status === 'active' && status.isExpiringsSoon) {
          this.showBanner.set(true);
        } else {
          this.showBanner.set(false);
        }
      },
      error: (error) => {
        console.error('Fehler beim Laden des Abo-Status:', error);
        this.showBanner.set(false);
      }
    });
  }

  closeBanner() {
    this.showBanner.set(false);
  }

  onRenew() {
    this.router.navigate(['/subscription-management']);
  }
}

